import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../shared/services/api';
import { useTranslation } from '../store/languageStore';

interface FieldDefinition {
  type: string;
  required: boolean;
  source: string;
  description: string;
  default?: any;
}

interface ATLASStructure {
  success: boolean;
  structure_info: any;
  position_fields: Record<string, FieldDefinition>;
  json_template: any;
  required_fields: string[];
  documentation: {
    description: string;
    usage: string[];
    official_spec: string;
    structure_location: string;
  };
}

const ATLASStructureViewer: React.FC = () => {
  const { t, tString } = useTranslation();
  const [atlasStructure, setAtlasStructure] = useState<ATLASStructure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  useEffect(() => {
    loadATLASStructure();
  }, []);

  const loadATLASStructure = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/admin/atlas-structure');
      setAtlasStructure(response.data);
    } catch (err: any) {
      console.error('Failed to load ATLAS structure:', err);
      setError(err.response?.data?.detail || tString('atlasStructure.loadFailed'));
      toast.error(tString('atlasStructure.toasts.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, itemName: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedItem(itemName);
        toast.success(tString('atlasStructure.toasts.copySuccess', itemName));
        setTimeout(() => setCopiedItem(null), 2000);
      }).catch(err => {
        console.error('Failed to copy:', err);
        toast.error(tString('atlasStructure.toasts.copyFailed'));
      });
    } else {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedItem(itemName);
        toast.success(tString('atlasStructure.toasts.copySuccess', itemName));
        setTimeout(() => setCopiedItem(null), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        toast.error(tString('atlasStructure.toasts.copyFailed'));
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !atlasStructure) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || t('atlasStructure.loadFailed')}
      </Alert>
    );
  }

  const { position_fields, json_template, required_fields, documentation } = atlasStructure;

  return (
    <Box>
      {/* Documentation Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {documentation.description}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>{t('atlasStructure.documentation.officialSpec')}:</strong> {documentation.official_spec}
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>{t('atlasStructure.documentation.location')}:</strong> {documentation.structure_location}
        </Typography>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          {t('atlasStructure.documentation.usage')}:
        </Typography>
        <ul style={{ marginTop: 0, paddingLeft: 20 }}>
          {documentation.usage.map((item, index) => (
            <li key={index}><Typography variant="body2">{item}</Typography></li>
          ))}
        </ul>
      </Alert>

      {/* Position Fields Table */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            {t('atlasStructure.sections.positionFields', Object.keys(position_fields).length)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('atlasStructure.table.fieldName')}</strong></TableCell>
                  <TableCell><strong>{t('atlasStructure.table.description')}</strong></TableCell>
                  <TableCell><strong>{t('atlasStructure.table.type')}</strong></TableCell>
                  <TableCell><strong>{t('atlasStructure.table.required')}</strong></TableCell>
                  <TableCell><strong>{t('atlasStructure.table.source')}</strong></TableCell>
                  <TableCell><strong>{t('atlasStructure.table.default')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(position_fields).map(([fieldName, fieldInfo]) => (
                  <TableRow
                    key={fieldName}
                    sx={{
                      backgroundColor: fieldInfo.required ? 'rgba(255, 152, 0, 0.08)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                        {fieldName}
                      </Typography>
                    </TableCell>
                    <TableCell>{fieldInfo.description}</TableCell>
                    <TableCell>
                      <Chip
                        label={fieldInfo.type}
                        size="small"
                        color={fieldInfo.type === 'string' ? 'primary' : fieldInfo.type === 'float' ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {fieldInfo.required ? (
                        <Chip label={tString('atlasStructure.labels.required')} size="small" color="warning" />
                      ) : (
                        <Chip label={tString('atlasStructure.labels.optional')} size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fieldInfo.source}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {fieldInfo.default !== undefined ? String(fieldInfo.default) : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Required Fields Only */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            {t('atlasStructure.sections.requiredFieldsOnly', required_fields.length)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {required_fields.map((field) => (
              <Chip
                key={field}
                label={field}
                color="warning"
                variant="outlined"
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* JSON Template */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            {t('atlasStructure.sections.jsonTemplate')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box position="relative">
            <Tooltip title={copiedItem === 'json_template' ? t('atlasStructure.actions.copied') : t('atlasStructure.actions.copyTemplate')}>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(JSON.stringify(json_template, null, 2), 'JSON Template')}
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
              >
                {copiedItem === 'json_template' ? <CheckIcon color="success" /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                maxHeight: 500,
                overflow: 'auto'
              }}
            >
              <pre style={{ margin: 0, fontSize: 12, fontFamily: 'monospace' }}>
                {JSON.stringify(json_template, null, 2)}
              </pre>
            </Paper>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Field Type Summary */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            {t('atlasStructure.sections.fieldTypeSummary')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" gap={2}>
            <Chip
              label={tString('atlasStructure.fieldCounts.string', Object.values(position_fields).filter(f => f.type === 'string').length)}
              color="primary"
            />
            <Chip
              label={tString('atlasStructure.fieldCounts.float', Object.values(position_fields).filter(f => f.type === 'float').length)}
              color="secondary"
            />
            <Chip
              label={tString('atlasStructure.fieldCounts.array', Object.values(position_fields).filter(f => f.type === 'array').length)}
              color="default"
            />
            <Chip
              label={tString('atlasStructure.fieldCounts.required', required_fields.length)}
              color="warning"
            />
            <Chip
              label={tString('atlasStructure.fieldCounts.optional', Object.keys(position_fields).length - required_fields.length)}
              color="default"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Actions */}
      <Box mt={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={() => copyToClipboard(JSON.stringify(atlasStructure, null, 2), tString('atlasStructure.actions.completeStructure'))}
        >
          {tString('atlasStructure.actions.copyCompleteStructure')}
        </Button>
        <Button
          variant="outlined"
          onClick={loadATLASStructure}
        >
          {tString('common.refresh')}
        </Button>
      </Box>
    </Box>
  );
};

export default ATLASStructureViewer;
